import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'

export interface Anchor {
  selector: string | null
  rx: number
  ry: number
  snippet: string
}
export interface ReviewComment {
  id: string
  createdAt: string
  page: string
  author: string
  user_id: string
  url: string
  app_version: string
  text: string
  device: 'phone' | 'tablet' | 'computer'
  viewport: number
  anchor: Anchor
  doc: { x: number; y: number }
  status: 'open' | 'resolved'
  doneAt: string | null
}
interface CommentPage {
  comments: ReviewComment[]
  next_cursor: string | null
}
const EMPTY_COMMENTS: ReviewComment[] = []

async function listComments(page: string | undefined, signal: AbortSignal) {
  const comments: ReviewComment[] = []
  const seen = new Set<string>()
  let cursor: string | null = null
  do {
    const params = new URLSearchParams()
    if (page !== undefined) params.set('page', page)
    if (cursor) params.set('cursor', cursor)
    const result: CommentPage = await apiFetch('/api/review-comments?' + params, { signal })
    comments.push(...result.comments)
    cursor = result.next_cursor
    if (cursor && seen.has(cursor)) throw new Error('Comments could not be fully loaded')
    if (cursor) seen.add(cursor)
  } while (cursor)
  return comments
}

/** Never show a previous account's or page's feedback while a new read loads. */
export function useReviewComments(page?: string) {
  const session = useAuthSession()
  const [refresh, setRefresh] = useState(0)
  const key = JSON.stringify([session, page ?? null, refresh])
  const [result, setResult] = useState<{
    key: string
    comments: ReviewComment[]
    error?: unknown
  } | null>(null)
  useEffect(() => {
    if (!session) return
    const controller = new AbortController()
    listComments(page, controller.signal)
      .then((comments) => {
        if (!controller.signal.aborted) setResult({ key, comments })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setResult({ key, comments: [], error })
      })
    return () => controller.abort()
  }, [session, page, key])
  const current = result?.key === key ? result : null
  return {
    comments: current?.comments ?? EMPTY_COMMENTS,
    loading: !!session && !current,
    error: current?.error,
    key,
    reload: () => setRefresh((value) => value + 1),
    update: (change: (comments: ReviewComment[]) => ReviewComment[]) =>
      setResult((previous) =>
        previous?.key === key ? { ...previous, comments: change(previous.comments) } : previous
      ),
  }
}

export function commentHref(comment: ReviewComment) {
  return `${comment.page}#review-comment-${encodeURIComponent(comment.id)}`
}

/** The served entry bundle identifies the actual frontend, including cached builds. */
export function appVersion() {
  const entry = [...document.scripts].find((script) => {
    if (script.type !== 'module' || !script.src) return false
    const url = new URL(script.src)
    return url.origin === window.location.origin && /\/assets\/[^/]+\.js$/.test(url.pathname)
  })
  return entry ? new URL(entry.src).pathname.split('/').pop()! : 'development'
}
